import { useState, useCallback, useMemo } from "react";
import { gql, useMutation } from "@apollo/client";
import { OrderPage_GetExistingOrderQuery, OrderPageTransactions_OrderFragment } from "../../../codegen/graphql";
import { Button } from "../common/Button";
import { Select } from "../common/LeboncoinSelect";
import { AddOrEditTransactionModal } from "./AddOrEditTransactionModal";
import { useRequestManager } from "../common/RequestManager";
import { formatCurrency } from "../../utils/currency";
import dayjs from "dayjs";
import { FaEdit, FaTrash } from "react-icons/fa";

type Transaction = OrderPageTransactions_OrderFragment['transactions'][number];

export default function OrderPageTransactions({
  order,
}: {
  order: NonNullable<OrderPage_GetExistingOrderQuery['order']>;
}) {
  const { trackRequest } = useRequestManager();
  const [showModal, setShowModal] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState<string | undefined>(undefined);
  const [lineItemFilter, setLineItemFilter] = useState<string>("");

  const lineItemFilterOptions = useMemo(() => {
    const base = [{ identifier: "", label: "All line items" }];
    const lineItems = order.orderProductsVariations ?? [];
    const fromLineItems = lineItems.map((opv) => ({
      identifier: opv.id,
      label: `${opv.id}: ${opv.adVariation?.ad?.title}`,
    }));
    return [...base, ...fromLineItems];
  }, [order.orderProductsVariations]);

  const filteredTransactions = useMemo(() => {
    const list = order.transactions ?? [];
    if (!lineItemFilter) return list;
    return list.filter((t) => {
      return (t.lineItemId ?? 0) === parseInt(lineItemFilter);
    });
  }, [order.transactions, lineItemFilter]);

  const [deleteTransaction] = useMutation(OrderPageTransactions_DeleteTransaction, {
    refetchQueries: ['OrderPage_GetExistingOrder'],
  });

  const handleDelete = useCallback(async (transaction: Transaction) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this transaction?\n\nKey: ${transaction.id || 'N/A'}\nAmount: ${formatCurrency(transaction.amount, transaction.currency || 'EUR')}\nSource: ${transaction.source || 'N/A'}\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    trackRequest(
      deleteTransaction({
        variables: { id: transaction.id }
      }),
      {
        title: 'Deleting transaction',
        isBlocking: false,
      }
    );
  }, [deleteTransaction, trackRequest]);

  const handleEdit = useCallback((transactionId: string) => {
    setEditingTransactionId(transactionId);
    setShowModal(true);
  }, []);

  const handleAdd = useCallback(() => {
    setEditingTransactionId(undefined);
    setShowModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditingTransactionId(undefined);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setShowModal(false);
    setEditingTransactionId(undefined);
  }, []);

  const handleDeleteAllTransactions = useCallback(() => {
    const confirmed = window.confirm(
      'Are you sure you want to delete all transactions?'
    );
    if (!confirmed) return;

    trackRequest(
      Promise.all(filteredTransactions.map((transaction) => (
        deleteTransaction({ variables: { id: transaction.id } })
      ))),
      {
        title: 'Deleting all transactions',
        isBlocking: false,
      }
    );
  }, [filteredTransactions, deleteTransaction, trackRequest]);

  return (
    <>
      <div className="flex flex-row justify-start items-end gap-4 mb-4">
        <Button 
          preset="primary" 
          size="md" 
          onClick={handleAdd}
        >
          Add Transaction
        </Button>
        <Button
          preset="primary"
          size="md"
          onClick={handleDeleteAllTransactions}
        >
          Delete All Transactions
        </Button>
        <div className="min-w-[240px]">
          <Select
            label="Filter by line item"
            options={lineItemFilterOptions}
            value={lineItemFilter}
            onChange={setLineItemFilter}
          />
        </div>
        <div className="flex-1" />
        <p className="text-lg text-gray-800 text-right">
          Total: {formatCurrency(filteredTransactions?.reduce((acc, t) => acc + t.amount, 0) || 0, 'EUR')}
          <br />
          Total VAT: {formatCurrency(filteredTransactions?.reduce((acc, t) => acc + t.amountVat, 0) || 0, 'EUR')}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Header Row */}
        <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-200">
          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Type
          </div>
          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            References
          </div>
          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Line Item
          </div>
          <div className="col-span-1 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Amount
          </div>
          <div className="col-span-1 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            VAT
          </div>
          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Origin
          </div>
          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Destination
          </div>
          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Source
          </div>
          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Bill Date
          </div>

          <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Comment
          </div>
          <div className="col-span-1 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Confirmed
          </div>
          <div className="col-span-1 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </div>
        </div>

        {/* Data Rows */}
        <div className="bg-white">
          {filteredTransactions.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {!order.transactions || order.transactions.length === 0
                ? "No transactions found for this order."
                : "No transactions match the selected line item filter."}
            </div>
          ) : (
            filteredTransactions.map((transaction) => {
              return (
              <div
                key={transaction.id}
                className="grid grid-cols-12 border-b border-gray-200 hover:bg-gray-50 transition-colors text-base"
              >
                <div className="col-span-1 px-4 py-2 text-gray-900 uppercase break-all">
                  {transaction.type || '-'}
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900 break-all">
                  {transaction.references.map((reference) => (
                    `${reference.relatedIdType}: ${reference.relatedId}`
                  )).join(', ')}<br/>
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900 break-all">
                  {transaction.lineItemId || '-'}<br/>
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900 text-right">
                  {formatCurrency(transaction.amount, transaction.currency || 'EUR')}
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900 text-right">
                  {formatCurrency(transaction.amountVat, transaction.currency || 'EUR')}
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900 text-right">
                  {transaction.originCountry || '-'}
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900 text-right">
                  {transaction.destinationCountry || '-'}
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900">
                  {transaction.source || '-'}
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900">
                  {transaction.billDate 
                    ? dayjs(transaction.billDate).format('DD MMM YYYY, HH:mm')
                    : '-'}
                </div>
                <div className="col-span-1 px-4 py-2 text-gray-900">
                  <div className="truncate" title={transaction.comment || undefined}>
                    {transaction.comment || '-'}
                  </div>
                </div>
                <div className="col-span-1 px-4 py-2 text-center">
                  {transaction.isConfirmed ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      No
                    </span>
                  )}
                </div>
                <div className="col-span-1 px-4 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      preset="white"
                      size="sm"
                      onClick={() => handleEdit(transaction.id)}
                      tooltip="Edit transaction"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      preset="danger-light"
                      size="sm"
                      onClick={() => handleDelete(transaction)}
                      tooltip="Delete transaction"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <AddOrEditTransactionModal
          order={order}
          transactionId={editingTransactionId}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}

export const ORDER_PAGE_TRANSACTIONS_ORDER_FRAGMENT = gql`
  fragment OrderPageTransactions_Order on Order {
    orderProductsVariations {
      id
      adVariation {
        ad {
          title
        }
      }
    }
    transactions {
      id
      orderId
      lineItemId
      type
      key
      references {
        relatedId
        relatedIdType
      }
      currency
      amount
      amountVat
      billDate
      comment
      source
      isConfirmed
      originCountry
      destinationCountry
      createdAt
      updatedAt
    }
  }
`;

const OrderPageTransactions_CreateTransaction = gql`
  mutation OrderPageTransactions_CreateTransaction($input: CreateTransactionInput!) {
    createTransaction(input: $input) {
      id
      orderId
      lineItemId
      type
      currency
      amount
      amountVat
      billDate
      comment
      source
      isConfirmed
      createdAt
      updatedAt
    }
  }
`;

const OrderPageTransactions_UpdateTransaction = gql`
  mutation OrderPageTransactions_UpdateTransaction($id: ID!, $input: UpdateTransactionInput!) {
    updateTransaction(id: $id, input: $input) {
      id
      orderId
      lineItemId
      type
      currency
      amount
      amountVat
      billDate
      comment
      source
      isConfirmed
      createdAt
      updatedAt
    }
  }
`;

const OrderPageTransactions_DeleteTransaction = gql`
  mutation OrderPageTransactions_DeleteTransaction($id: ID!) {
    deleteTransaction(id: $id)
  }
`;
